<template>
  <div
    v-if="activeLabels.length || $slots.before"
    ref="labelContainer"
    v-resize="computeVisibleCount"
    class="relative"
  >
    <div
      ref="labelRow"
      class="flex items-end flex-shrink min-w-0 gap-y-1 overflow-hidden"
    >
      <slot name="before" />
      <woot-label
        v-for="(label, index) in activeLabels"
        :key="label.id"
        :title="label.title"
        :description="label.description"
        :color="label.color"
        class="max-w-[calc(100%-0.5rem)]"
        :class="{ 'invisible absolute': index >= visibleCount }"
        small
      />
    </div>
    <div v-if="hiddenLabels.length" class="relative">
      <span
        v-if="variant === 'arrow'"
        ref="triggerBadge"
        class="material-symbols-outlined cursor-pointer text-base/4 text-gray-500 rounded-[4px] hover:bg-gray-200"
        @mouseover="showTooltip"
        @mouseleave="showHiddenLabels = false"
      >
        stat_minus_1
      </span>
      <div v-else class="bg-white rounded-lg flex items-center">
        <span
          ref="triggerBadge"
          class="semantic-tag small tag-mute-01 cursor-pointer whitespace-nowrap"
          @mouseover="showTooltip"
          @mouseleave="showHiddenLabels = false"
        >
          {{ hiddenLabels.length }}+
        </span>
      </div>
      <div
        class="tooltip-popup"
        :class="showHiddenLabels ? 'flex' : 'hidden'"
        :style="tooltipStyle"
      >
        <div class="tooltip-arrow" />
        <div
          class="bg-white rounded-[8px] px-2 py-1 flex flex-col gap-1 w-max min-w-[120px] max-w-[240px] items-start"
        >
          <woot-label
            v-for="label in hiddenLabels"
            :key="label.id"
            :title="label.title"
            :description="label.description"
            :color="label.color"
            class="max-w-[calc(100%-0.25rem)] break-all relative z-[1]"
            small
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import conversationLabelMixin from 'dashboard/mixins/conversation/labelMixin';

// each label carries an `mr-1` (`ml-1` in RTL), which offsetWidth does not include
const LABEL_SPACING = 4;
// used until the "N+" badge is in the DOM and can be measured
const BADGE_WIDTH_FALLBACK = 32;

export default {
  mixins: [conversationLabelMixin],
  props: {
    conversationId: {
      type: Number,
      required: true,
    },
    conversationLabels: {
      type: Array[String],
      required: false,
      default: '',
    },
    variant: {
      type: String,
      default: 'badge',
    },
  },
  data() {
    return {
      showHiddenLabels: false,
      tooltipStyle: {},
      // show everything until the first measurement narrows it down
      visibleCount: Infinity,
    };
  },
  computed: {
    hiddenLabels() {
      return this.activeLabels.slice(this.visibleCount);
    },
  },
  watch: {
    activeLabels() {
      this.$nextTick(() => this.computeVisibleCount());
    },
  },
  mounted() {
    // the resize observer only reports back after a debounce, which leaves the
    // labels overflowing on the first paint of a virtual list item
    this.$nextTick(() => this.computeVisibleCount());
  },
  methods: {
    async checkFetchLabels() {
      if (!this.savedLabels.length) {
        await this.$store.dispatch(
          'conversationLabels/get',
          this.conversationId
        );
      }
    },
    // Shows as many labels as fit in the space left over by the timestamp.
    // Labels beyond `visibleCount` stay in the DOM as `invisible absolute` so
    // they remain measurable here.
    computeVisibleCount() {
      const container = this.$refs.labelContainer;
      const row = this.$refs.labelRow;
      if (!container || !row) return;

      const available = container.clientWidth;
      if (!available) return;

      const labelWidths = [];
      let occupied = 0;
      Array.from(row.children).forEach(child => {
        if (child.classList.contains('label')) {
          labelWidths.push(child.offsetWidth + LABEL_SPACING);
        } else {
          // anything rendered through the `before` slot
          occupied += child.offsetWidth;
        }
      });

      const countThatFits = reserved => {
        let used = occupied + reserved;
        let count = 0;
        for (let index = 0; index < labelWidths.length; index += 1) {
          if (used + labelWidths[index] > available) break;
          used += labelWidths[index];
          count += 1;
        }
        return count;
      };

      let count = countThatFits(0);
      // leave room for the "N+" badge once we know something has to be hidden
      if (count < labelWidths.length) {
        count = countThatFits(this.badgeWidth());
      }
      // a single label wider than the row is clipped rather than dropped,
      // otherwise the card would show a bare "1+" and nothing else
      this.visibleCount = Math.max(count, 1);
    },
    badgeWidth() {
      const badge = this.$refs.triggerBadge;
      return badge
        ? badge.offsetWidth + LABEL_SPACING
        : BADGE_WIDTH_FALLBACK + LABEL_SPACING;
    },
    showTooltip() {
      const rect = this.$refs.triggerBadge.getBoundingClientRect();
      this.tooltipStyle = {
        position: 'fixed',
        left: `${rect.left + rect.width / 2}px`,
        top: `${rect.top + rect.height + 8}px`,
        transform: 'translateX(-50%)',
      };
      this.showHiddenLabels = true;
    },
    onShowLabels(e) {
      e.stopPropagation();
      this.showHiddenLabels = !this.showHiddenLabels;
    },
  },
};
</script>

<style lang="scss" scoped>
.show-more--button {
  @apply h-5;
  &.secondary:focus {
    @apply text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-700;
  }
}

.labels-wrap {
  .secondary {
    @apply border border-solid border-slate-100 dark:border-slate-700;
  }
}

.tooltip-popup {
  @apply flex-col items-center;
  z-index: 9999;
  filter: drop-shadow(0px 1px 2px rgba(17, 24, 39, 0.1))
    drop-shadow(0px 1px 1px rgba(17, 24, 39, 0.06));
}

.tooltip-arrow {
  width: 12px;
  height: 12px;
  background: white;
  border-radius: 2px;
  transform: rotate(45deg);
  margin-bottom: -6px;
  flex-shrink: 0;
  position: relative;
  z-index: 0;
}
</style>
